// Shape-only diagnostics for blank AIFC database rows. AFM only sends the
// `document`; this records its shape at stream completion, never its raw content.

// At or below this length a finalized `document` is treated as effectively blank.
const BLANK_DOCUMENT_LENGTH_THRESHOLD = 2;

// The database `document` is XML-tagged CSV: a `<fields>` block (header
// `field_name,type,configuration` + one line per column) then data rows.
const FIELDS_OPEN_TAG = '<fields>';
const FIELDS_CLOSE_TAG = '</fields>';
const FIELDS_HEADER = 'field_name,type,configuration';

/** `ok` means no problem; every other value names a likely blank-row cause. */
type AifcDatabaseDocumentCategory =
	| 'ok'
	| 'emptyDocument' // blank / whitespace / near-empty -> whole database blank
	| 'commaPrefixed' // starts with ',' -> mid-stream reset / malformed continuation
	| 'missingFieldsBlock' // no <fields>...</fields> -> receiver ignores everything
	| 'malformedFieldsHeader' // <fields> present but header line missing/wrong
	| 'noDataRows' // fields defined but zero data rows -> no entries created
	| 'emptyDataCells' // data row(s) present but all cell values blank -> blank rows
	| 'columnCountMismatch'; // data row column count != header -> dropped values

type AifcDatabaseDocumentDiagnostics = {
	source: 'aifcDatabaseDocumentCategory';
	category: AifcDatabaseDocumentCategory;
	documentLength: string;
	fieldCount: string;
	dataRowCount: string;
	emptyDataRowCount: string;
	contentId: string;
};

const firstNonWhitespaceChar = (value: string): string | null => {
	const match = value.match(/\S/);
	return match ? match[0] : null;
};

const isBlankDocument = (rawDocument: string): boolean =>
	rawDocument.trim().length <= BLANK_DOCUMENT_LENGTH_THRESHOLD;

const extractBlock = (rawDocument: string, openTag: string, closeTag: string): string | null => {
	const start = rawDocument.indexOf(openTag);
	if (start === -1) {
		return null;
	}
	const end = rawDocument.indexOf(closeTag, start + openTag.length);
	if (end === -1) {
		return null;
	}
	return rawDocument.slice(start + openTag.length, end);
};

const nonEmptyLines = (block: string): string[] =>
	block
		.split('\n')
		.map((line) => line.trim())
		.filter((line) => line.length > 0);

/** Splits a CSV line into cells, respecting simple double-quoted fields. */
const splitCsvLine = (line: string): string[] => {
	const cells: string[] = [];
	let current = '';
	let inQuotes = false;
	for (const char of line) {
		if (char === '"') {
			inQuotes = !inQuotes;
		} else if (char === ',' && !inQuotes) {
			cells.push(current);
			current = '';
		} else {
			current += char;
		}
	}
	cells.push(current);
	return cells;
};

/** True when every cell in the row is blank. */
const rowHasNoValues = (line: string): boolean =>
	splitCsvLine(line).every((cell) => cell.trim().length === 0);

/** Classifies a database `document` into a blank-row category with shape-only counts. */
export const categorizeAifcDatabaseDocument = (
	rawDocument: string,
	contentId: string,
): AifcDatabaseDocumentDiagnostics => {
	const firstChar = firstNonWhitespaceChar(rawDocument);

	const base = {
		source: 'aifcDatabaseDocumentCategory' as const,
		documentLength: String(rawDocument.length),
		contentId,
	};

	const build = (
		category: AifcDatabaseDocumentCategory,
		fieldCount = 0,
		dataRowCount = 0,
		emptyDataRowCount = 0,
	): AifcDatabaseDocumentDiagnostics => ({
		...base,
		category,
		fieldCount: String(fieldCount),
		dataRowCount: String(dataRowCount),
		emptyDataRowCount: String(emptyDataRowCount),
	});

	if (isBlankDocument(rawDocument)) {
		return build('emptyDocument');
	}
	if (firstChar === ',') {
		return build('commaPrefixed');
	}

	const fieldsBlock = extractBlock(rawDocument, FIELDS_OPEN_TAG, FIELDS_CLOSE_TAG);
	if (fieldsBlock === null) {
		return build('missingFieldsBlock');
	}

	const fieldsLines = nonEmptyLines(fieldsBlock);
	if (fieldsLines.length === 0 || fieldsLines[0] !== FIELDS_HEADER) {
		return build('malformedFieldsHeader');
	}

	const fieldCount = fieldsLines.length - 1; // exclude the header line
	if (fieldCount === 0) {
		return build('malformedFieldsHeader', fieldCount);
	}

	// Data rows: non-empty, non-XML-tag lines after the </fields> tag.
	const afterFields = rawDocument.slice(
		rawDocument.indexOf(FIELDS_CLOSE_TAG) + FIELDS_CLOSE_TAG.length,
	);
	const dataLines = nonEmptyLines(afterFields).filter(
		(line) => !line.startsWith('<') && !line.startsWith('</'),
	);

	if (dataLines.length === 0) {
		return build('noDataRows', fieldCount);
	}

	const emptyDataRowCount = dataLines.filter(rowHasNoValues).length;
	if (emptyDataRowCount === dataLines.length) {
		return build('emptyDataCells', fieldCount, dataLines.length, emptyDataRowCount);
	}

	const mismatchedRow = dataLines.some((line) => splitCsvLine(line).length !== fieldCount);
	if (mismatchedRow) {
		return build('columnCountMismatch', fieldCount, dataLines.length, emptyDataRowCount);
	}

	return build('ok', fieldCount, dataLines.length, emptyDataRowCount);
};
